/**
 * @typedef InvoiceRecord
 * @property {Number} id
 * @property {String} created
 * @property {String} created_friendly
 * @property {String} total Number formatted currency string, e.g. $123.45
 * @property {Number} total_pence
 * @property {Number} is_paid
 * @property {String} stripe_invoice_id
 */

/**
 * @typedef GetInvoicesResponse
 * @property {Boolean} status
 * @property {object} data
 * @property {Number} data.total
 * @property {Number} data.per_page
 * @property {Number} data.current_page
 * @property {Number} data.last_page
 * @property {Number} data.last_page
 * @property {InvoiceRecord[]} data.invoices
 */

/**
 * @typedef Purchaser
 * @property {Number?} nInvoices
 */

export function PurchaseCredits() {
    const Bark = window.Bark = window.Bark || {};
    const seller = Bark.seller = Bark.seller || {};
    /** @type {Purchaser} purchaser */
    const purchaser = seller.purchaser = seller.purchaser || {};
    purchaser.nInvoices = undefined;

    /**
     * @returns {Promise<GetInvoicesResponse|{status:false}>}
     */
    const getInvoices = async () => {
        try {
            /** @type { GetInvoicesResponse } */
            return await Bark.api('/seller/invoices/1/');
        } catch (e) {
            return { status: false };
        }
    };

    const countInvoices = async () => {
        const invoices = await getInvoices();

        if (invoices.status) {
            return invoices.data.total;
        }

        return 0;
    }

    /**
     * Purchase credits and then potentially purchase a lead on success
     * @param {String|Number} creditPackId
     * @param {Boolean} setAutoTopUp
     * @param {String|null} stripeToken
     * @param {Number|false} projectId
     * @param {String|false} completedPaymentIntentId
     * @param {Function|Null} onCreditsPurchased
     */
    seller.purchaseCreditsThroughApi = async function (
        creditPackId,
        setAutoTopUp,
        stripeToken,
        projectId,
        completedPaymentIntentId = false,
        onCreditsPurchased = null,
    ) {
        Bark.seller.showProjectInteractionLoader();

        if (!stripeToken) {
            await Bark.seller.getSellerCurrentCardInfo();
        }
        const payload = {
            autoTopupSetting: setAutoTopUp,
            sToken: stripeToken,
            paymentIntentId: completedPaymentIntentId || null,
            paymentMethodId: stripeToken ? null : Bark.seller.cardId || null,
            tracking: Bark.getTrackableParameters(),
        };

        if (Bark.seller.getIsPurchasing() && !completedPaymentIntentId) {
            Bark.seller.hideProjectInteractionLoader();
            return;
        }

        Bark.seller.setPurchasing(true);

        Bark.api(
            `creditpack/${creditPackId}/purchase/`,
            payload,
            async function (e) {
                const isOneClick = Bark.seller.lastAttemptedResponseType === 'oneClickResponse';

                Bark.seller.setPurchasing(false);
                Bark.seller.lastPurchasedPackId = creditPackId;
                Bark.seller.mostRecentCreditsPurchased = e.data.n_credits;

                if (projectId !== false) {
                    Bark.seller.submitYourDetailsCall(projectId, isOneClick);
                }

                if (Bark.seller.show_ghg_post_starter_pack) {
                    purchaser.nInvoices = await countInvoices();
                }

                onCreditsPurchased && onCreditsPurchased();
            },
            function (error) {
                const errorResponse = error.responseJSON;

                if (errorResponse?.error?.type === 'action_required') {
                    Bark.seller.handleScaAuthenticationRequest(
                        errorResponse?.error?.error_data?.client_secret,
                        (paymentIntent) => {
                            Bark.seller.purchaseCreditsThroughApi(
                                creditPackId,
                                setAutoTopUp,
                                null,
                                projectId,
                                paymentIntent.id,
                                onCreditsPurchased,
                            );
                        }, Bark.seller.isMultiPaymentModalAvailable() ? (err) => {
                            Bark.seller.purchaseUpsellHandleFailed(
                                _t('seller_dashboard:credit-upsell.error-message.authorize-payment'),
                            );
                            Bark.seller.hideProjectInteractionLoader();
                        } : null
                    );
                } else {
                    Bark.seller.setPurchasing(false);
                    Bark.seller.hideProjectInteractionLoader();
                    Bark.seller.handleCardDecline(error);
                }
            },
            'POST',
        );
    }
}
